import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCircleArrowRight,
    faCircleArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import "../scss/_memberCarousel.scss";
import variables from "../scss/_variables.module.scss";
import throttle from "./throttle";

const { slidesShown } = variables;
const middleIndex = Math.floor(slidesShown / 2);

function MemberCarousel() {
    const { t, i18n } = useTranslation();
    const slides = i18n.getResourceBundle(
        i18n.language,
        "membersTranslationContainer"
    ).membersTranslation;

    const [currentSet, setCurrentSet] = useState(0);
    const slideContainerRef = useRef(null);

    const handleNext = () => {
        setCurrentSet((prevSet) =>
            prevSet + 1 >= slides.length ? 0 : prevSet + 1
        );
    };

    const handlePrev = () => {
        setCurrentSet((prevSet) =>
            prevSet - 1 < 0 ? slides.length - 1 : prevSet - 1
        );
    };

    const handleKeyDown = (event) => {
        if (event.key === "ArrowRight") {
            handleNext();
        } else if (event.key === "ArrowLeft") {
            handlePrev();
        }
    };

    const throttledHandleKeyDown = throttle(handleKeyDown, 100);

    const handleScroll = (event) => {
        if (event.deltaX > 0) {
            handleNext();
        } else if (event.deltaX < 0) {
            handlePrev();
        }
    };

    const throttledHandleScroll = throttle(handleScroll, 900);

    useEffect(() => {
        window.addEventListener("keydown", throttledHandleKeyDown);

        const slideContainer = slideContainerRef.current;
        slideContainer.addEventListener("wheel", throttledHandleScroll);

        return () => {
            window.removeEventListener("keydown", throttledHandleKeyDown);
            slideContainer.removeEventListener("wheel", throttledHandleScroll);
        };
    }, []);

    const handleDotClick = (dotIndex) => {
        let distanceFromMiddle;
        if (dotIndex === middleIndex) {
            return;
        } else if (dotIndex < middleIndex) {
            distanceFromMiddle = middleIndex - dotIndex;
            for (let i = 0; i < distanceFromMiddle; i++) {
                setTimeout(() => {
                    handlePrev();
                }, 300 * i);
            }
        } else if (dotIndex > middleIndex) {
            distanceFromMiddle = dotIndex - middleIndex;
            for (let i = 0; i < distanceFromMiddle; i++) {
                setTimeout(() => {
                    handleNext();
                }, 300 * i);
            }
        }
    };

    const getDisplayedSlides = () => {
        if (!slidesShown) return [];
        const displayedSlides = [];

        for (let i = -middleIndex; i <= middleIndex; i++) {
            const index = (currentSet + i + slides.length) % slides.length;
            displayedSlides.push(slides[index]);
        }

        return displayedSlides;
    };

    const displayedSlides = getDisplayedSlides();

    return (
        <div className="container">
            <h2 className="heading">{t("memberCarouselTitle")}</h2>
            <div className="main">
                <div className="body" ref={slideContainerRef}>
                    <div className="b-placeholder"></div>

                    {displayedSlides.map((slide, index) => (
                        <Link
                            to={`/Team/${slide.id}`} // Use slide.id for routing to the SoloPage
                            key={slide.id}
                            className={`slide${index === middleIndex ? " middle" : index === middleIndex - 1 ? " left" : index === middleIndex + 1 ? " right" : ""}`}
                        >
                            <img
                                src={require(`../assets/${slide.src}`)}
                                alt={slide.alt}
                            />
                            <div className="caption">
                                <h3>{`${slide.fName} ${slide.lName}`}</h3>
                                <p>{slide.degree}</p>
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="button-placeholder"></div>

                <div className="buttons">
                    <button onClick={handlePrev} className="leftB">
                        <FontAwesomeIcon
                            icon={faCircleArrowLeft}
                            className="leftA"
                        />
                    </button>
                    <div className="slider-container">
                        <div className="slider-buttons">
                            {Array.from({ length: slidesShown }).map(
                                (_, index) => (
                                    <button
                                        key={index}
                                        className={`${index === middleIndex ? "dot active" : "dot"}`}
                                        onClick={() => handleDotClick(index)}
                                    ></button>
                                )
                            )}
                        </div>
                    </div>

                    <button onClick={handleNext} className="rightB">
                        <FontAwesomeIcon
                            icon={faCircleArrowRight}
                            className="rightA"
                        />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default MemberCarousel;
